import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney } from '../brand/brand';
import type { Deal } from '../api/types';
import { markPhotoBroken, useStandInPhoto } from '../lib/standInPhotos';

/**
 * A deal in the grid, per Direction B.
 *
 * The price row is pinned to the bottom and kept on ONE line so that cards in a
 * row baseline-align — the handoff calls this out, and it is the difference
 * between a grid that looks built and one that looks assembled.
 *
 * WHAT THE DESIGN SHOWS THAT THE API DOES NOT HAVE: a star rating, a review
 * count, and a units-sold figure. There is no review system and no sold counter
 * exposed on the marketplace DTO. Inventing "★ 4.8 · 1,240 bought" would be
 * fabricating social proof on a page whose job is to make people trust us with
 * money, so those elements are omitted until the data is real.
 */
export function DealCard({ deal, compact = false }: { deal: Deal; compact?: boolean }) {
  // A photo that fails to load is treated like a deal with none. Found 2026-10-01:
  // six demo deals named files that were never uploaded, and the grid showed
  // broken-image boxes to every visitor.
  const [broken, setBroken] = useState(false);

  // No working photo of its own: borrow one (lib/standInPhotos.ts). The placeholder
  // shows only while the catalogue loads, or when there is nothing to borrow.
  const needsStandIn = deal.imageUrl === null || broken;
  const standIn = useStandInPhoto(deal.id, deal.category, [deal.imageUrl], needsStandIn);
  const src = needsStandIn ? standIn : deal.imageUrl;

  function onPhotoError(url: string) {
    markPhotoBroken(url);
    if (!needsStandIn) setBroken(true);
  }

  return (
    <Link to={`/deals/${deal.id}`} className={compact ? 'card small' : 'card'}>
      <div className="media">
        {src === null
          ? <div className="ph">{compact ? '' : 'deal photo'}</div>
          /* alt="" — the title sits directly below, so announcing the image too
             makes a screen reader say the same thing twice. */
          : <img key={src} src={src} alt="" loading="lazy" onError={() => onPhotoError(src)} />}

        {deal.savingsPercent > 0 && (
          /* aria-hidden: the price row below states the saving in words. Hearing
             "25% off" twice per card across a grid is noise, not information. */
          <span className="badge-discount" aria-hidden="true">{deal.savingsPercent}% off</span>
        )}

        {deal.remaining === 'last_few' && (
          <span className="badge-urgent" aria-hidden="true">Ending soon</span>
        )}
      </div>

      <div className="body">
        {!compact && (
          <div className="merchant-row">
            <span className="merchant-tile" aria-hidden="true" />
            <span className="merchant-name">{deal.businessName}</span>
          </div>
        )}

        <h3>{deal.title}</h3>

        {!compact && <p className="meta-row">{deal.category}</p>}

        <div className="price-row">
          <span className="price">{formatMoney(deal.priceCents)}</span>
          <s className="was">
            <span className="visually-hidden">Worth </span>
            {formatMoney(deal.faceValueCents)}
          </s>
          <span className="save-chip">
            <span className="visually-hidden">You save </span>
            −{formatMoney(deal.savingsCents)}
          </span>
        </div>
      </div>
    </Link>
  );
}
